// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Core Game Configuration - Consolidated Small Configs
 * 
 * This file consolidates small configuration modules for better bundle efficiency:
 * - Economy & Credits (was EconomyConfig.js)
 * - Fuel System (was FuelConfig.js) 
 * - Warp Travel (was WarpConfig.js)
 * - UI Constants (was UIConfig.js)
 * - Game Settings (was GameSettingsConfig.js)
 * 
 * Universe-specific configs remain separate to preserve uniqueness.
 * Large configs (>5KB) remain separate for maintainability.
 */

// ============================================================================
// ECONOMY & CREDITS CONFIGURATION
// ============================================================================
export const economy = {
  // Starting credits amount
  startingCredits: 1000,
  
  // Service costs (in credits)
  refuelCostPerPercent: 1,            // Cost per 1% fuel (100 credits for full refuel from 0%)
  weaponReloadCostPerCharge: 0.2,     // Cost per 1% weapon charge (20 credits for full reload from 0%)
  repairCostPerHP: 5,                 // Cost per HP to repair systems
  towRefuelPercent: 75,               // Free tow service refuels to this percentage
  
  // Upgrade costs (in credits)
  // Note: Fuel tank upgrade costs are now defined in UpgradesConfig.js
  
  // Weapon upgrade costs
  weaponUpgradeTier1: 300,
  weaponUpgradeTier2: 600,
  weaponUpgradeTier3: 1200,
  
  // Engine upgrade costs
  engineUpgradeTier1: 300,
  engineUpgradeTier2: 600,
  engineUpgradeTier3: 1200,
  
  // Scanner upgrade costs
  scannerUpgradeTier1: 300,
  scannerUpgradeTier2: 600,
  scannerUpgradeTier3: 1200,
  
  // Hull upgrade costs
  hullUpgradeTier1: 300,
  hullUpgradeTier2: 600,
  hullUpgradeTier3: 1200,
  
  // Event rewards (in credits)
  combatVictoryMin: 50,
  combatVictoryMax: 150,
  scanBonusMin: 30,
  scanBonusMax: 100,
  explorationResourcesMin: 100,
  explorationResourcesMax: 500
};

// ============================================================================
// FUEL SYSTEM CONFIGURATION
// ============================================================================
export const fuel = {
  // Starting fuel amount (percentage)
  startingFuel: 100,
  
  // Maximum fuel capacity (percentage) - can be upgraded
  maxFuelCapacity: 100,
  
  // Base fuel consumption per light-year (percentage)
  // 0.33% per LY means ~300 LY range on full tank
  fuelPerLightYear: 0.33,
  
  // Warp multiplier increase per warp level (15% = 0.15)
  warpFuelMultiplier: 0.15,
  
  // Safety margin added to fuel calculations (10% = 0.1)
  safetyMargin: 1.1,
  
  // Fuel level considered "low" for warnings (percentage)
  lowFuelThreshold: 20,
  
  // Fuel level considered "critical" for warnings (percentage)
  criticalFuelThreshold: 10,
  
  // Fuel depot refuel cost (percentage of current fuel)
  depotRefuelCost: 20,
  
  // Weapon reload fuel cost per weapon (percentage)
  weaponReloadCostPerWeapon: 2,
  
  // Tank upgrade settings (now handled by UpgradesConfig.js)
  maxTankUpgrades: 2,            // Maximum number of upgrades (fuel_tank_1 and fuel_tank_2)
  
  // Regeneration rate in orbit (% per second)
  regenRateOrbit: 2.0
};

// ============================================================================
// WARP TRAVEL CONFIGURATION
// ============================================================================
export const warp = {
  // Minimum travel time in seconds (prevents instant travel)
  minTravelTime: 5,
  
  // Maximum travel time in seconds (prevents tedious long trips)
  maxTravelTime: 30,
  
  // Travel time scaling factor
  travelTimeScaleFactor: 2,
  
  // Maximum safe warp factor (no damage risk)
  maxSafeWarp: 7,
  
  // Warp 8 engine damage settings
  warp8: {
    damageChance: 0.3,        // 30% chance
    minDamage: 5,             // Minimum HP damage
    maxDamage: 15,            // Maximum HP damage
    checkProgressPercent: 25  // Check at 25% progress
  },
  
  // Warp 9 engine damage settings
  warp9: {
    damageChance: 0.6,        // 60% chance
    minDamage: 10,            // Minimum HP damage
    maxDamage: 25,            // Maximum HP damage
    checkProgressPercent: 25  // Check at 25% progress
  },
  
  // AUTO mode warp selection thresholds (light-years)
  autoModeThresholds: {
    warp2Max: 10,    // < 10 LY → Warp 2
    warp3Max: 50,    // < 50 LY → Warp 3
    warp4Max: 200,   // < 200 LY → Warp 4
    warp5Max: 500,   // < 500 LY → Warp 5
    warp6Min: 500    // >= 500 LY → Warp 6
  },
  
  // Warp gate travel time (seconds)
  gateTravelTime: 5  // Fixed duration for dimensional gate travel
};

// ============================================================================
// UI CONFIGURATION
// ============================================================================

// Z-Index Hierarchy
// Using a scale of 100s to allow for easy insertion of new layers
export const Z_INDEX = {
  // Background layers (negative values)
  BACKGROUND: -1,
  BACKGROUND_DEEP: -2,

  // Base game elements
  BASE: 1,
  STAR_MAP_GRID: 0,
  STAR_MAP_LABELS: 10,

  // HUD and panels (100-500)
  HUD: 300,
  NAVIGATION_PANEL: 150,
  SYSTEM_STATUS: 110,
  COMPACT_STATUS: 10,

  // Expanded views (5000-9999)
  EXPANDED_STAR_MAP: 9000,
  EXPANDED_STAR_MAP_CONTROLS: 9010,
  EXPANDED_STAR_MAP_TOOLTIP: 9020,

  // Full-screen effects (10000-19999)
  WARP_EFFECT: 10000,
  WARP_EFFECT_TEXT: 10100,
  ARRIVAL_SCREEN: 10000,
  GRAVITY_WELL: 10000,
  GRAVITY_WELL_TEXT: 10010,
  DOCK_SCREEN: 10000,
  PLANET_EXPLORATION: 10500,

  // Modals and shops (20000-29999)
  UPGRADE_SHOP: 20000,
  UPGRADE_SHOP_CONFIRM: 25000,
  WEAPON_SHOP: 20000,
  TRADE_SHOP: 20000,
  MODULES_SHOP: 20000,

  // System modals (30000-39999)
  SYSTEM_STATUS_MODAL: 30000,
  SYSTEM_STATUS_REPAIR_CONFIRM: 30002,

  // Critical overlays (100000+)
  RESET_CONFIRM: 100000,
  CHEAT_MESSAGE: 100001,
  CRITICAL_ALERT: 9999
};

// UI Constants
export const UI_CONSTANTS = {
  // Animation durations (ms)
  FADE_DURATION: 300,
  MODAL_SLIDE_DURATION: 300,
  WARP_DURATION: 2000,

  // Transition timing
  STANDARD_TRANSITION: '0.3s ease',
  FAST_TRANSITION: '0.15s ease',
  SLOW_TRANSITION: '0.5s ease',

  // Breakpoints
  MOBILE_BREAKPOINT: 768,
  TABLET_BREAKPOINT: 1024,
  DESKTOP_BREAKPOINT: 1440,

  // Color thresholds
  OPTIMAL_THRESHOLD: 80,
  WARNING_THRESHOLD: 60,
  CRITICAL_THRESHOLD: 30
};

// UI Configuration (for GameConfig.ui compatibility)
export const uiConfig = {
  // Z-Index management
  zIndex: Z_INDEX,
  
  // Distance calculation
  distance: {
    pixelToLightYearScale: 10 // Converts pixel distance to light years
  },
  
  // Status color thresholds
  statusColors: {
    optimalThreshold: 80,  // Above this = green
    warningThreshold: 60,  // Above this = orange
    criticalThreshold: 30  // Below this = red
  },
  
  // Animation settings
  animations: {
    planetStarCount: 100,    // Number of stars in planet background
    warpStarCount: 400,      // Base number of warp effect particles
    fadeInDuration: 300,     // Standard fade-in duration (ms)
    fadeOutDuration: 300     // Standard fade-out duration (ms)
  },
  
  // Course calculation speeds
  courseCalculation: {
    normalSpeed: 50,      // ms per calculation step (normal mode)
    emergencySpeed: 20    // ms per calculation step (emergency mode)
  },
  
  // Feedback display
  feedback: {
    displayDuration: 3000  // How long to show feedback messages (ms)
  },
  
  // Visual effects
  sparkGenerationChance: 0.3,      // Chance to generate sparks on critical systems
  easterEggChance: 0.001,          // Chance for easter egg in scan results
  anomalySystemSelection: 0.5      // Threshold for system damage selection
};

// ============================================================================
// GAME SETTINGS CONFIGURATION
// ============================================================================
export const gameSettingsConfig = {
  // ============================================================================
  // DIFFICULTY PRESETS
  // ============================================================================
  difficulty: {
    easy: {
      fuelConsumptionMultiplier: 0.7,   // 30% less fuel consumption
      damageMultiplier: 0.5,            // 50% less damage taken
      repairCostMultiplier: 0.5,        // 50% cheaper repairs
      warp8DamageChance: 0.15,          // 15% instead of 30%
      warp9DamageChance: 0.30           // 30% instead of 60%
    },
    
    normal: {
      fuelConsumptionMultiplier: 1.0,
      damageMultiplier: 1.0,
      repairCostMultiplier: 1.0,
      warp8DamageChance: 0.30,
      warp9DamageChance: 0.60
    },
    
    hard: {
      fuelConsumptionMultiplier: 1.5,   // 50% more fuel consumption
      damageMultiplier: 1.5,            // 50% more damage taken
      repairCostMultiplier: 2.0,        // 2x repair costs
      warp8DamageChance: 0.45,          // 45% instead of 30%
      warp9DamageChance: 0.80           // 80% instead of 60%
    }
  },

  // ============================================================================
  // SPECIAL UPGRADE UNLOCK COSTS
  // ============================================================================
  specialUpgrades: {
    emergencyWarpCost: 600,             // Cost to unlock emergency warp ability
    autoRepairCost: 600                 // Cost to unlock auto-repair system
  },

  // ============================================================================
  // SHOP INVENTORY SYSTEM
  // ============================================================================
  shopInventory: {
    // Shop items available at each location
    // Format: locationName: ['ITEM_TYPE', 'ITEM_TYPE', ...]
    // Item types: 'REFUEL', 'TANK_UPGRADE', 'WEAPON_RELOAD', 'WEAPONS', 'UPGRADES'
    
    // Stations - Full service
    'WAYPOINT STATION ALPHA': ['REFUEL', 'TANK_UPGRADE', 'WEAPON_RELOAD', 'WEAPONS', 'UPGRADES'],
    'DEEP SPACE STATION MIDWAY': ['REFUEL', 'TANK_UPGRADE', 'WEAPON_RELOAD', 'WEAPONS', 'UPGRADES'],
    'TRADING POST NEXUS': ['REFUEL', 'TANK_UPGRADE', 'WEAPON_RELOAD', 'WEAPONS', 'UPGRADES'],
    'FRONTIER OUTPOST': ['REFUEL', 'TANK_UPGRADE', 'WEAPON_RELOAD', 'WEAPONS', 'UPGRADES'],
    
    // Planets - No tank upgrades (limited infrastructure)
    'SOL (EARTH)': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'PROXIMA CENTAURI': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'BARNARD\'S STAR': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'LUYTEN\'S STAR': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'WOLF 359': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'LALANDE 21185': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'SIRIUS': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'EPSILON ERIDANI': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'ROSS 128': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    '61 CYGNI': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'PROCYON': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'STRUVE 2398': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'GROOMBRIDGE 34': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'HD 110067': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'EPSILON DRACONIS': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'POLARIS': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'BETELGEUSE': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS'],
    'RIGEL': ['REFUEL', 'WEAPON_RELOAD', 'WEAPONS']
  },

  // ============================================================================
  // CHEAT CODES
  // ============================================================================
  cheats: {
    enabled: true,                      // Enable/disable cheat codes globally
    
    // Fuel cheat
    fuel: {
      enabled: true,
      keyCombo: 'Alt+a',                // Key combination (case-insensitive)
      amount: 20,                       // Fuel units to add
      description: 'Add 20% fuel'
    },
    
    // Credits cheat
    credits: {
      enabled: true,
      keyCombo: 'Alt+c',                // Key combination (case-insensitive)
      amount: 500,                      // Credits to add
      description: 'Add 500 credits'
    },
    
    // Repair all systems cheat
    repair: {
      enabled: true,
      keyCombo: 'Alt+r',                // Key combination (case-insensitive)
      description: 'Repair all systems to 100%'
    }
  }
};

// ============================================================================
// CONSOLIDATED EXPORTS
// ============================================================================

// Export consolidated config object
export const coreGameConfig = {
  economy,
  fuel,
  warp,
  ui: uiConfig,
  gameSettings: gameSettingsConfig
};

// Default export
export default coreGameConfig;